import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6'
import { Outlet } from 'react-router-dom'
import { WelcomeRoute } from './WelcomeRoute'
import { AppProvider } from '../AppProvider'
import { ErrorRoute } from '@/routes/ErrorRoute'
import { SelectAccountRoute } from '@/routes/SelectAccountRoute'
import { AuthProviderLayout } from '@/components/layouts/AuthProviderLayout'
import { PageLayout } from '@/components/layouts/PageLayout'
import {
  InitialPageLoadHydrateFallback,
  StaleClientBundleErrorElement,
} from '@joyful/ui/app'
import { StaticOrgScopedRoutes } from './OrgScopedRoutes'

function ProtectedRouteElement() {
  return (
    <AuthProviderLayout>
      <Outlet />
    </AuthProviderLayout>
  )
}

function RootLayout() {
  return (
    <AppProvider>
      <Outlet />
    </AppProvider>
  )
}

const staleClientBundleErrorElement = (
  <StaleClientBundleErrorElement fallback={<ErrorRoute error={500} />} />
)

export const router = createBrowserRouter([
  {
    errorElement: staleClientBundleErrorElement,
    lazy: async () => {
      const { CliCallbackRoute } = await import('@/routes/CliCallbackRoute')
      return { Component: CliCallbackRoute }
    },
    path: '/cli-auth/callback',
  },
  {
    HydrateFallback: InitialPageLoadHydrateFallback,
    children: [
      {
        children: [
          {
            index: true,
            lazy: async () => {
              const { LandingPageRedirectRoute } =
                await import('./LandingPageRedirectRoute')
              return { Component: LandingPageRedirectRoute }
            },
          },

          {
            element: <WelcomeRoute />,
            path: 'welcome',
          },

          {
            element: <SelectAccountRoute />,
            path: 'select-account',
          },

          {
            children: [
              // Wave 1B routes (announcements, agent-phone-calls, work-blocks/rules,
              // prompts, agent-skills) moved to internal-web.

              {
                lazy: async () => {
                  const { AccountRoute } = await import('./AccountRoute')
                  return { Component: AccountRoute }
                },
                path: 'account/*',
              },
              {
                lazy: async () => {
                  const { GlobalAnalysisRoute } =
                    await import('./GlobalAnalysisRoute')
                  return { Component: GlobalAnalysisRoute }
                },
                path: 'analysis/:analysisId',
              },
              // org-scoped routes
              {
                children: [
                  ...StaticOrgScopedRoutes,
                  {
                    lazy: async () => {
                      const { ScopedEncounterRedirectRoute } =
                        await import('./CatchallOrgFallbackRoute')
                      return { Component: ScopedEncounterRedirectRoute }
                    },
                    path: ':encounterId',
                  },
                  {
                    index: true,
                    lazy: async () => {
                      const { CatchallOrgFallbackRoute } =
                        await import('./CatchallOrgFallbackRoute')
                      return { Component: CatchallOrgFallbackRoute }
                    },
                    path: '*',
                  },
                ],
                path: ':org_slug',
              },
            ],
            element: <PageLayout />,
          },
        ],
        element: <ProtectedRouteElement />,
      },

      {
        lazy: async () => {
          const { SignInRoute } = await import('./SignInRoute')
          return { Component: SignInRoute }
        },
        path: 'sign-in/*',
      },

      {
        lazy: async () => {
          const { ExtensionAuthCompleteRoute } =
            await import('./ExtensionAuthCompleteRoute')
          return { Component: ExtensionAuthCompleteRoute }
        },
        path: 'extension/auth-complete',
      },
    ],
    element: <RootLayout />,
    errorElement: staleClientBundleErrorElement,
    path: '/',
  },
])
